import { produce } from 'immer';
import { DashboardResource, GridDefinition, GridItemDefinition, PanelDefinition } from '@/core';
import { GRID_ITEM_HEIGHT, GRID_LAYOUT_COLS, GRID_LAYOUT_SMALL_BREAKPOINT } from '../constants';
import { getUniquePanelKey } from './panelUtils';

/**
 * This adds the panel to a new panel group at the top of the dashboard.
 */
export function addPanelToDashboardConfig(dashboard: DashboardResource, panel: PanelDefinition) {
  return produce(dashboard, (draft) => {
    const panelKey = getUniquePanelKey({ panels: draft.spec.panels });
    draft.spec.panels[panelKey] = panel;
    draft.spec.layouts.unshift({
      kind: 'Grid',
      spec: {
        display: {
          title: 'New Panel Group',
        },
        items: [
          {
            content: {
              $ref: `#/spec/panels/${panelKey}`,
            },
            height: 6,
            width: 12,
            x: 0,
            y: 0,
          },
        ],
      },
    });
  });
}

/**
 * This adds the panel to an existing group in the dashboard.
 */
export function addPanelToGroupInDashboardConfig(
  /**
   * The original unmodified dashboard.
   */
  dashboard: DashboardResource,
  /**
   * The panel definition of the new panel being added to the dashboard.
   */
  panel: PanelDefinition,
  /**
   * The index of the panel group that the panel is being added to.
   */
  panelGroupIndex: number
) {
  return produce(dashboard, (draft) => {
    const panelKey = getUniquePanelKey({ panels: draft.spec.panels });
    // Add panel to the list of panels
    draft.spec.panels[panelKey] = panel;
    // Put the panel at the beginning of the specified panel group
    if (draft.spec.layouts[panelGroupIndex] && draft.spec.layouts[panelGroupIndex]?.kind === 'Grid') {
      (draft.spec.layouts[panelGroupIndex] as GridDefinition)?.spec.items?.unshift({
        content: {
          $ref: `#/spec/panels/${panelKey}`,
        },
        height: 6,
        width: 12,
        x: 0,
        y: 0,
      });
    }
  });
}

export interface AddPanelGroupToDashboardConfigArgs {
  /**
   * The original unmodified dashboard.
   */
  dashboard: DashboardResource;
  /**
   * The panel definitions to add to the dashboard.
   */
  panels: PanelDefinition[];
  /**
   * The name of the panel group to add the panels to.
   */
  panelGroupName: string;
  /**
   * If provided, the panels will be added with the provided layout format.
   * @default SimpleLayout
   */
  layout?: SimpleLayout | Array<Omit<GridItemDefinition, 'content'>>;
}

export interface SimpleLayout {
  /**
   * The number of panels per row to add to the dashboard.
   * Every panel will be the same width and height.
   * It will add the panels to the dashboard in the order they are provided.
   * @default 3
   */
  panelsPerRow?: number;
}

function isSimpleLayout(layout?: SimpleLayout | Array<Omit<GridItemDefinition, 'content'>>): layout is SimpleLayout {
  return typeof layout === 'object' && 'panelsPerRow' in layout;
}

const DEFAULT_PANELS_PER_ROW = 3;

function getSimpleLayout(index: number, panelsPerRow: number = DEFAULT_PANELS_PER_ROW) {
  const panelWidth = Math.floor(GRID_LAYOUT_COLS[GRID_LAYOUT_SMALL_BREAKPOINT] / panelsPerRow);
  return {
    x: (index % panelsPerRow) * panelWidth,
    y: Math.floor(index / panelsPerRow),
    width: panelWidth,
    height: GRID_ITEM_HEIGHT,
  };
}

export function addPanelGroupToDashboardConfig({
  dashboard,
  panels: panelsToAdd,
  panelGroupName,
  layout: initialLayout,
}: AddPanelGroupToDashboardConfigArgs) {
  const gridPanelItems: GridItemDefinition[] = [];

  const dashboardPanels = { ...dashboard.spec.panels };

  const simpleLayout = isSimpleLayout(initialLayout);

  for (let i = 0; i < panelsToAdd.length; i++) {
    const panelToAdd = panelsToAdd[i];
    if (!panelToAdd) continue;
    let layoutPosition: Omit<GridItemDefinition, 'content'>;

    if (simpleLayout || !initialLayout) {
      const panelsPerRow =
        initialLayout?.panelsPerRow ??
        // If panelsPerRow is not provided, and there are fewer than 3 panels to add, show all panels in one row
        (panelsToAdd.length < DEFAULT_PANELS_PER_ROW ? panelsToAdd.length : DEFAULT_PANELS_PER_ROW);
      layoutPosition = getSimpleLayout(i, panelsPerRow);
    } else {
      layoutPosition = initialLayout?.[i] ?? getSimpleLayout(i, DEFAULT_PANELS_PER_ROW);
    }

    const panelKey = getUniquePanelKey({ panels: dashboardPanels });
    gridPanelItems.push({
      content: {
        $ref: `#/spec/panels/${panelKey}`,
      },
      ...layoutPosition,
    });
    dashboardPanels[panelKey] = panelToAdd;
  }
  return produce(dashboard, (draft) => {
    draft.spec.panels = dashboardPanels;
    // Add the panel group to the top of the dashboard
    draft.spec.layouts.unshift({
      kind: 'Grid',
      spec: {
        display: {
          title: panelGroupName,
        },
        items: gridPanelItems,
      },
    });
  });
}
